// pages/reports/CustomerReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
    ChevronUp, 
    RotateCcw, 
    Download,
    Printer,
    DollarSign,
    
    TrendingDown,
    PieChart,
    FileText,
   
    Users,
    ShoppingBag,
    CreditCard,
    RefreshCw,
   
    Search,
    Phone,
    Mail,
    Award
} from 'feather-icons-react/build/IconComponents';
import { reportActions } from '../../core/action/reportActions';

const MySwal = withReactContent(Swal);

const CustomerReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    
    // State
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('summary');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('total_sales');
    
    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.getCustomerReport(params);
            
            if (result.success) {
                setReportData(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);
    
    // Load on mount and when dependencies change
    useEffect(() => {
        loadReport();
    }, [loadReport]);
    
    // Handle export PDF
    const handleExportPDF = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToPDF('customers', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `customer_report_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export PDF',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle export Excel
    const handleExportExcel = async () => {
        try {
            const params = {
                from_date: formatDateForAPI(fromDate),
                to_date: formatDateForAPI(toDate)
            };
            
            const result = await reportActions.exportToExcel('customers', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `customer_report_${formatDateForAPI(fromDate)}_to_${formatDateForAPI(toDate)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting Excel:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export Excel',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle print
    const handlePrint = () => {
        window.print();
    };
    
    // Filter and sort customers
    const getFilteredCustomers = () => {
        if (!reportData?.customer_breakdown) return [];
        
        let filtered = reportData.customer_breakdown;
        
        if (searchTerm) {
            filtered = filtered.filter(customer => 
                customer.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.phone?.includes(searchTerm) ||
                customer.email?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        // Sort
        filtered.sort((a, b) => {
            if (sortBy === 'total_sales') return b.total_sales - a.total_sales;
            if (sortBy === 'sale_count') return b.sale_count - a.sale_count;
            if (sortBy === 'avg_value') return (b.avg_sale_value || 0) - (a.avg_sale_value || 0);
            return 0;
        });
        
        return filtered;
    };
    
    // Render filter bar
    const renderFilterBar = () => {
        const sortOptions = [
            { value: 'total_sales', label: 'By Total Sales' },
            { value: 'sale_count', label: 'By Purchase Frequency' },
            { value: 'avg_value', label: 'By Average Value' }
        ];
        
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-2">
                            <label className="form-label">From Date</label>
                            <DatePicker
                                selected={fromDate}
                                onChange={setFromDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={toDate}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">To Date</label>
                            <DatePicker
                                selected={toDate}
                                onChange={setToDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                minDate={fromDate}
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Search Customer</label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Search size={16} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Sort By</label>
                            <Select
                                options={sortOptions}
                                value={sortOptions.find(opt => opt.value === sortBy)}
                                onChange={(opt) => setSortBy(opt?.value || 'total_sales')}
                                placeholder="Sort by"
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={loadReport}>
                                <RefreshCw size={16} className="me-2" />
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render summary cards
    const renderSummaryCards = () => {
        if (!reportData) return null;
        
        const { summary } = reportData;
        
        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Customers</p>
                                    <h3 className="mb-0 text-primary">
                                        {summary.total_customers}
                                    </h3>
                                    <small className="text-muted">All customers</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <Users size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Active Customers</p>
                                    <h3 className="mb-0 text-success">
                                        {summary.active_customers}
                                    </h3>
                                    <small className="text-muted">Made purchases</small>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <Award size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Sales</p>
                                    <h3 className="mb-0 text-info">
                                        {reportActions.formatCurrency(summary.total_sales_amount)}
                                    </h3>
                                    <small className="text-muted">{summary.total_sales} invoices</small>
                                </div>
                                <div className="bg-info bg-opacity-10 rounded-circle p-3">
                                    <ShoppingBag size={24} className="text-info" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Outstanding Due</p>
                                    <h3 className="mb-0 text-danger">
                                        {reportActions.formatCurrency(summary.total_due)}
                                    </h3>
                                    <small className="text-muted">Pending payments</small>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <TrendingDown size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render tabs
    const renderTabs = () => {
        const tabs = [
            { id: 'summary', label: 'Summary', icon: <PieChart size={16} /> },
            { id: 'customers', label: 'Customer Details', icon: <Users size={16} /> },
            { id: 'products', label: 'Products by Customer', icon: <ShoppingBag size={16} /> },
            { id: 'payments', label: 'Payment Methods', icon: <CreditCard size={16} /> },
            { id: 'credits', label: 'Customer Credits', icon: <DollarSign size={16} /> }
        ];
        
        return (
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span className="ms-2">{tab.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    };
    
    // Render summary tab
    const renderSummaryTab = () => {
        if (!reportData) return null;
        
        const topCustomers = getFilteredCustomers().slice(0, 5);
        
        return (
            <div className="row">
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Customer Overview</h6>
                        </div>
                        <div className="card-body">
                            <table className="table table-sm">
                                <tbody>
                                    <tr>
                                        <td>Total Registered Customers</td>
                                        <td className="text-end"><strong>{reportData.summary.total_customers}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Active Customers (Period)</td>
                                        <td className="text-end"><strong>{reportData.summary.active_customers}</strong></td>
                                    </tr>
                                    <tr>
                                        <td>Inactive Customers</td>
                                        <td className="text-end">
                                            <strong>{reportData.summary.total_customers - reportData.summary.active_customers}</strong>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Average Sales per Customer</td>
                                        <td className="text-end">
                                            <strong>
                                                {reportData.summary.active_customers > 0 
                                                    ? reportActions.formatCurrency(reportData.summary.total_sales_amount / reportData.summary.active_customers)
                                                    : reportActions.formatCurrency(0)}
                                            </strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-6 mb-4">
                    <div className="card h-100">
                        <div className="card-header">
                            <h6 className="mb-0">Top Customers</h6>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Customer</th>
                                            <th className="text-end">Sales</th>
                                            <th className="text-end">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {topCustomers.map((customer, idx) => (
                                            <tr key={idx}>
                                                <td>{customer.customer_name}</td>
                                                <td className="text-end">{customer.sale_count}</td>
                                                <td className="text-end">{reportActions.formatCurrency(customer.total_sales)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render customers tab
    const renderCustomersTab = () => {
        const filteredCustomers = getFilteredCustomers();
        
        if (filteredCustomers.length === 0) {
            return (
                <div className="alert alert-info">
                    No customers found matching your search.
                </div>
            );
        }
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Customer Performance Details</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Customer</th>
                                    <th>Contact</th>
                                    <th className="text-end">Purchases</th>
                                    <th className="text-end">Total Sales</th>
                                    <th className="text-end">Paid</th>
                                    <th className="text-end">Due</th>
                                    <th className="text-end">Avg. Value</th>
                                    <th>Last Purchase</th>
                                    <th>First Purchase</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredCustomers.map((customer, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{customer.customer_name}</strong>
                                        </td>
                                        <td>
                                            {customer.phone && (
                                                <div><small><Phone size={12} /> {customer.phone}</small></div>
                                            )}
                                            {customer.email && (
                                                <div><small><Mail size={12} /> {customer.email}</small></div>
                                            )}
                                        </td>
                                        <td className="text-end">{customer.sale_count}</td>
                                        <td className="text-end">{reportActions.formatCurrency(customer.total_sales)}</td>
                                        <td className="text-end text-success">{reportActions.formatCurrency(customer.total_paid)}</td>
                                        <td className="text-end text-danger">{reportActions.formatCurrency(customer.total_due)}</td>
                                        <td className="text-end">
                                            {customer.sale_count > 0 
                                                ? reportActions.formatCurrency(customer.total_sales / customer.sale_count)
                                                : '-'}
                                        </td>
                                        <td>{reportActions.formatDate(customer.last_purchase_date)}</td>
                                        <td>{reportActions.formatDate(customer.first_purchase_date)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render products tab
    const renderProductsTab = () => {
        if (!reportData?.product_breakdown || reportData.product_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No product data available for the selected period.
                </div>
            );
        }
        
        // Group products by customer
        const productsByCustomer = {};
        reportData.product_breakdown.forEach(item => {
            if (!productsByCustomer[item.customer_id]) {
                productsByCustomer[item.customer_id] = {
                    customer_name: item.customer_name,
                    products: []
                };
            }
            productsByCustomer[item.customer_id].products.push(item);
        });
        
        return (
            <div className="accordion" id="customerProductsAccordion">
                {Object.values(productsByCustomer).map((customer, idx) => (
                    <div className="accordion-item mb-3" key={idx}>
                        <h2 className="accordion-header">
                            <button
                                className="accordion-button collapsed"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#collapse${idx}`}
                            >
                                <strong>{customer.customer_name}</strong>
                                <span className="badge bg-primary ms-2">
                                    {customer.products.length} products
                                </span>
                            </button>
                        </h2>
                        <div id={`collapse${idx}`} className="accordion-collapse collapse">
                            <div className="accordion-body">
                                <table className="table table-sm">
                                    <thead>
                                        <tr>
                                            <th>Product</th>
                                            <th>SKU</th>
                                            <th className="text-end">Quantity</th>
                                            <th className="text-end">Total Spent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customer.products.map((product, pIdx) => (
                                            <tr key={pIdx}>
                                                <td>{product.product_name}</td>
                                                <td>{product.sku}</td>
                                                <td className="text-end">{product.total_quantity}</td>
                                                <td className="text-end">{reportActions.formatCurrency(product.total_spent)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };
    
    // Render payments tab
    const renderPaymentsTab = () => {
        if (!reportData?.payment_breakdown || reportData.payment_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No payment data available for the selected period.
                </div>
            );
        }
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Payment Methods by Customer</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Customer</th>
                                    <th>Payment Method</th>
                                    <th className="text-end">Transactions</th>
                                    <th className="text-end">Total Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.payment_breakdown.map((payment, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{payment.customer_name}</strong>
                                        </td>
                                        <td>
                                            <span className="badge bg-secondary">
                                                {payment.payment_method?.replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td className="text-end">{payment.payment_count}</td>
                                        <td className="text-end">{reportActions.formatCurrency(payment.total_amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render credits tab
    const renderCreditsTab = () => {
        if (!reportData?.credit_breakdown || reportData.credit_breakdown.length === 0) {
            return (
                <div className="alert alert-info">
                    No credit data available for the selected period.
                </div>
            );
        }
        
        return (
            <div className="card">
                <div className="card-header bg-warning bg-opacity-10">
                    <h6 className="mb-0 text-warning">Customer Credits</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Customer</th>
                                    <th className="text-end">Credit Count</th>
                                    <th className="text-end">Total Credits</th>
                                    <th className="text-end">Pending Credits</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.credit_breakdown.map((credit, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{credit.customer_name}</strong>
                                        </td>
                                        <td className="text-end">{credit.credit_count}</td>
                                        <td className="text-end">{reportActions.formatCurrency(credit.total_credits)}</td>
                                        <td className="text-end text-warning">{reportActions.formatCurrency(credit.pending_credits)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Tooltip renderer
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);
    
    if (loading && !reportData) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Customer Report...</h5>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Customer Report</h4>
                            <h6>Analyze customer behavior and sales</h6>
                        </div>
                    </div>
                    
                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-danger" onClick={handleExportPDF}>
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success" onClick={handleExportExcel}>
                                    <FileText size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={loadReport}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>
                
                {/* Period Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Period:</strong> {reportActions.formatDate(reportData?.period.from_date)} to {reportActions.formatDate(reportData?.period.to_date)}
                                    </div>
                                    <div>
                                        <strong>Active Customers:</strong> {reportData?.summary.active_customers || 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Filter Bar */}
                {renderFilterBar()}
                
                {/* Summary Cards */}
                {renderSummaryCards()}
                
                {/* Tabs */}
                {renderTabs()}
                
                {/* Tab Content */}
                {activeTab === 'summary' && renderSummaryTab()}
                {activeTab === 'customers' && renderCustomersTab()}
                {activeTab === 'products' && renderProductsTab()}
                {activeTab === 'payments' && renderPaymentsTab()}
                {activeTab === 'credits' && renderCreditsTab()}
            </div>
        </div>
    );
};

export default CustomerReport;